import { FC, useEffect, useState } from "react";
import { InnerBox } from "@/components/elements/InnerBox";
import { Grid, SelectChangeEvent } from "@mui/material";
import { FormField } from "@/components/elements/FormField";
import { useFormContext, useWatch } from "react-hook-form";
import { TMatchForm } from "@/features/match/types/matchForm";
import { Select } from "@/components/elements/Select";
import { EMatchContractStatus } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { IconCalendarMatch } from "@/assets/icons/IconCalendarMatch";
import { EMPTY_STR } from "@/constants/string";

type TMatchContractFormProp = {};
export const MatchContractForm: FC<TMatchContractFormProp> = () => {
  const methods = useFormContext<TMatchForm>();
  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = methods;

  const contractStatus = watch("contractStatus");
  const contractStartDate = watch("contractStartDate");
  const [isFocused, setIsFocused] = useState(false);
  useEffect(() => {
    if (!isFocused && contractStartDate && !dayjs(contractStartDate).isValid()) {
      setValue("contractStartDate", null);
    }
  }, [contractStartDate, isFocused, setValue]);

  const trialEndDateWatchField = useWatch({ control, name: "trialEndDate" });
  const trialStartDateWatchField = useWatch({ control, name: "trialStartDate" });
  const contractEndDateWatchField = useWatch({ control, name: "contractEndDate" });
  const contractStartDateWatchField = useWatch({ control, name: "contractStartDate" });

  useEffect(() => {
    if (errors.trialEndDate) {
      trigger("trialEndDate");
    }
    if (errors.contractStatus) {
      trigger("contractStatus");
    }
    if (errors.trialStartDate) {
      trigger("trialStartDate");
    }
    if (errors.contractStartDate) {
      trigger("contractStartDate");
    }
    if (errors.contractEndDate) {
      trigger("contractEndDate");
    }
  }, [trialEndDateWatchField, trialStartDateWatchField, contractEndDateWatchField, contractStartDateWatchField]);

  const handleChangeContractStatus = (event: SelectChangeEvent<unknown>) => {
    if (event.target.value === EMatchContractStatus.TRIAL) {
      if (!trialStartDateWatchField) {
        methods.setValue("trialStartDate", dayjs(), {
          shouldValidate: true,
        });
      }
      if (!trialEndDateWatchField) {
        const newDate = dayjs(trialStartDateWatchField || dayjs()).add(2, "weeks");
        setValue("trialEndDate", newDate, {
          shouldValidate: true,
        });
      }
    }
    if (event.target.value === EMatchContractStatus.DURING_CONTRACT && !contractStartDateWatchField) {
      methods.setValue("contractStartDate", dayjs(), {
        shouldValidate: true,
      });
    }
  };
  return (
    <InnerBox title="契約情報">
      <Grid container rowSpacing={2} columnSpacing={3}>
        <Grid item xs={6}>
          <FormField control={control} name="contractStatus">
            <Select
              enumName="EMatchContractStatus"
              onChange={handleChangeContractStatus}
              placeholder="トライアル中/トライアル終了 / 契約中 / 解約済み"
            />
          </FormField>
        </Grid>
        <Grid item xs={6}></Grid>
        <Grid item xs={6}>
          <FormField control={control} name="contractPlan">
            <Input />
          </FormField>
        </Grid>
        <Grid item xs={6}></Grid>
        <Grid item xs={6}>
          <FormField control={control} name="trialStartDate">
            <InputDatePicker placeholder={EMPTY_STR.DATE} format="YYYY/MM/DD" customIcon={<IconCalendarMatch />} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="trialEndDate">
            <InputDatePicker placeholder={EMPTY_STR.DATE} format="YYYY/MM/DD" customIcon={<IconCalendarMatch />} />
          </FormField>
        </Grid>
        <Grid item xs={6} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}>
          <FormField control={control} name="contractStartDate" required={contractStatus === EMatchContractStatus.DURING_CONTRACT}>
            <InputDatePicker
              placeholder={EMPTY_STR.DATE}
              maxDate={contractEndDateWatchField ?? undefined}
              format="YYYY/MM/DD"
              customIcon={<IconCalendarMatch />}
            />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="contractEndDate">
            <InputDatePicker placeholder={EMPTY_STR.DATE} format="YYYY/MM/DD" customIcon={<IconCalendarMatch />} />
          </FormField>
        </Grid>
      </Grid>
    </InnerBox>
  );
};
